import { GatedQueryError } from "../components/GatedQueryError";
import {
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  DataTable,
  InlineStack,
  Layout,
  Page,
  Select,
  SkeletonBodyText,
  Text,
  TextField,
} from "@shopify/polaris";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  acquireStaleKitReplacementClaim,
  cancelStaleKitReplacementClaim,
  cleanupStaleKitAfterReplacement,
  deleteStaleKitLocalConfiguration,
  getKitBom,
  listBomVariants,
  listConfiguredKits,
  prepareStaleKitReplacementDraft,
  explicitlyApplyStaleKitReplacementAndReflect,
  replaceKitComponentsAndReflect,
  type BomComponentInput,
  type BomReplacementDraftProtocol,
  type BomVariantOption,
  type KitBom,
} from "../api/bom";
import { formatNumber } from "../utils/money";
import {
  assertCurrentShopGeneration,
  canonicalShopDomain,
  runInShopGeneration,
  ShopGenerationChangedError,
} from "../api/client";
import {
  editShopifyProductWithAdminIntent,
  ShopifyProductEditIntentError,
} from "../api/shopify_intents";
import {
  ensureShopifyOptionalScope,
  ShopifyOptionalScopeError,
} from "../api/shopify_scopes";
import { triggerSync } from "../api/sync";
import { getPlanPreview } from "../api/plan_previews";
import {
  canonicalStockyPublicBigIntId,
  type StockyPublicBigIntId,
} from "../api/stocky_public_ids";
import { PlanDataPreviewGate } from "../components/PlanDataPreviewGate";
import { useVerifiedShopGeneration } from "../ShopGenerationBoundary";
import {
  BomStaleReplacementCheckpointStorageError,
  clearBomStaleReplacementCheckpoint,
  loadBomStaleReplacementCheckpoint,
  saveBomStaleReplacementCheckpoint,
  transitionBomStaleReplacementCheckpoint,
  type BomCleanupCheckpoint,
  type BomStaleReplacementCheckpoint,
  type BomTargetWriteCheckpoint,
} from "../utils/bomStaleReplacementCheckpoint";

import { t } from "../lib/i18n";

const FEATURE_NAME = "セット商品の在庫";
// Component labels include product name, SKU, and current stock. At 320px the
// 1600px promo frame clipped ordinary current labels; this still shrinks to
// the available row width before InlineStack wraps on narrow screens.
export const BOM_COMPONENT_SELECT_WIDTH = "min(100%, 460px)";
const KITS_QUERY_KEY = ["bom-kits"];
const VARIANTS_QUERY_KEY = ["bom-variants"];
const MAX_SHOPIFY_BUNDLE_COMPONENTS = 30;

interface StaleKitReplacement {
  sourceKit: KitBom;
  candidates: BomVariantOption[];
  selectedTargetId: StockyPublicBigIntId | null;
  serverBound: boolean;
}

interface ReplacementNotice {
  tone: "info" | "critical" | "success";
  title: string;
  message: string;
}

interface PendingProductCatalogSync {
  sourceKit: KitBom;
  beforeVariantIds: string[];
  draftProtocol: BomReplacementDraftProtocol;
  claimedTargetShopifyVariantId: string | null;
  targetShopifyProductId: string | null;
  intentCompleted: boolean;
  editorClosed: boolean;
  allComponentsCurrent: boolean;
  components: BomComponentInput[];
}

type BomTargetCheckpoint =
  | BomTargetWriteCheckpoint
  | BomCleanupCheckpoint;

interface InitialReplacementCheckpointState {
  checkpoint: BomStaleReplacementCheckpoint | null;
  error: Error | null;
}

function loadInitialReplacementCheckpoint(
  shopGeneration: string,
): InitialReplacementCheckpointState {
  try {
    return {
      checkpoint: loadBomStaleReplacementCheckpoint(shopGeneration),
      error: null,
    };
  } catch (error) {
    return {
      checkpoint: null,
      error:
        error instanceof Error
          ? error
          : new Error("BOM replacement checkpoint is unavailable"),
    };
  }
}

// 🔴 テンプレートリテラルで組み立てると t() を通らず、英語UIでも「・在庫」と
//    全角カッコが残る（英語UIの描画検査だけがこれを見つけた）。文全体を辞書の
//    キーにして、SKU 有無で2本に分ける。
function variantLabel(option: BomVariantOption): string {
  const name = option.title ?? t("商品名なし");
  return option.sku
    ? t("{name}（{sku}） ・在庫{qty}", { name, sku: option.sku, qty: option.available })
    : t("{name} ・在庫{qty}", { name, qty: option.available });
}

function BomContent() {
  const shopGeneration = useVerifiedShopGeneration();
  const queryClient = useQueryClient();
  const [initialReplacementCheckpoint] = useState(() =>
    loadInitialReplacementCheckpoint(shopGeneration.shopGeneration),
  );
  const [replacementCheckpoint, setReplacementCheckpoint] =
    useState<BomStaleReplacementCheckpoint | null>(
      initialReplacementCheckpoint.checkpoint,
    );
  const [replacementCheckpointError, setReplacementCheckpointError] =
    useState<Error | null>(initialReplacementCheckpoint.error);
  const [selectedShopifyVariantId, setSelectedShopifyVariantId] =
    useState<StockyPublicBigIntId | null>(null);
  const [editorRevealRequest, setEditorRevealRequest] = useState(0);
  const [replacement, setReplacement] =
    useState<StaleKitReplacement | null>(null);
  const [replacementNotice, setReplacementNotice] =
    useState<ReplacementNotice | null>(null);
  const [creatingReplacementFor, setCreatingReplacementFor] =
    useState<StockyPublicBigIntId | null>(null);
  const [pendingProductCatalogSync, setPendingProductCatalogSync] =
    useState<PendingProductCatalogSync | null>(() => {
      const checkpoint = initialReplacementCheckpoint.checkpoint;
      return checkpoint?.phase === "intent-or-sync"
        ? {
            sourceKit: checkpoint.sourceKit,
            beforeVariantIds: checkpoint.beforeVariantIds,
            draftProtocol: checkpoint.draftProtocol,
            claimedTargetShopifyVariantId:
              checkpoint.claimedTargetShopifyVariantId,
            targetShopifyProductId: null,
            intentCompleted: checkpoint.intentCompleted,
            editorClosed: false,
            allComponentsCurrent: false,
            components: [],
          }
        : null;
    });
  const [replacementCommitLocked, setReplacementCommitLocked] =
    useState(false);
  const [deletingStaleIds, setDeletingStaleIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [staleDeleteErrors, setStaleDeleteErrors] = useState<
    Record<string, string>
  >({});
  const editorRef = useRef<HTMLDivElement | null>(null);
  const replacementStartInFlight = useRef(false);
  const legacyReconcileInFlight = useRef(false);
  const productEditIntentInFlight = useRef(false);
  const productSaveConfirmationInFlight = useRef(false);
  const productCatalogSyncInFlight = useRef(false);
  const restoredTargetCheckpoint = useRef(false);
  const staleDeleteInFlight = useRef<Set<string>>(new Set());

  const catalogQuery = useQuery({ queryKey: VARIANTS_QUERY_KEY, queryFn: () => listBomVariants() });
  const kitsQuery = useQuery({ queryKey: KITS_QUERY_KEY, queryFn: () => listConfiguredKits() });

  const catalog = catalogQuery.data ?? [];
  const currentShopifyVariantIds = new Set(
    catalog.map((variant) =>
      canonicalStockyPublicBigIntId(variant.shopify_variant_id),
    ),
  );
  const kitOptions = [
    { label: t("選択してください"), value: "" },
    ...catalog.map((v) => ({
      label: variantLabel(v),
      value: canonicalStockyPublicBigIntId(v.shopify_variant_id),
    })),
  ];
  const replacementSourceIds = new Set<string>();
  const replacementTargetIds = new Set<string>();
  if (replacementCheckpoint !== null) {
    replacementSourceIds.add(
      canonicalStockyPublicBigIntId(
        replacementCheckpoint.sourceKit.shopify_variant_id,
      ),
    );
    if (replacementCheckpoint.phase === "intent-or-sync") {
      if (replacementCheckpoint.claimedTargetShopifyVariantId !== null) {
        replacementTargetIds.add(
          canonicalStockyPublicBigIntId(
            replacementCheckpoint.claimedTargetShopifyVariantId,
          ),
        );
      }
    } else {
      replacementTargetIds.add(
        canonicalStockyPublicBigIntId(
          replacementCheckpoint.targetShopifyVariantId,
        ),
      );
    }
  }
  if (pendingProductCatalogSync !== null) {
    replacementSourceIds.add(
      canonicalStockyPublicBigIntId(
        pendingProductCatalogSync.sourceKit.shopify_variant_id,
      ),
    );
    if (pendingProductCatalogSync.claimedTargetShopifyVariantId !== null) {
      replacementTargetIds.add(
        canonicalStockyPublicBigIntId(
          pendingProductCatalogSync.claimedTargetShopifyVariantId,
        ),
      );
    }
  }
  if (replacement !== null) {
    replacementSourceIds.add(
      canonicalStockyPublicBigIntId(replacement.sourceKit.shopify_variant_id),
    );
    if (replacement.selectedTargetId !== null) {
      replacementTargetIds.add(
        canonicalStockyPublicBigIntId(replacement.selectedTargetId),
      );
    }
  }
  if (creatingReplacementFor !== null) {
    replacementSourceIds.add(
      canonicalStockyPublicBigIntId(creatingReplacementFor),
    );
  }
  const exactRecoveryTargetId =
    replacement?.selectedTargetId !== null &&
    replacement?.selectedTargetId !== undefined
      ? canonicalStockyPublicBigIntId(replacement.selectedTargetId)
      : replacementCheckpoint === null
        ? null
        : replacementCheckpoint.phase === "intent-or-sync"
          ? replacementCheckpoint.claimedTargetShopifyVariantId === null
            ? null
            : canonicalStockyPublicBigIntId(
                replacementCheckpoint.claimedTargetShopifyVariantId,
              )
          : canonicalStockyPublicBigIntId(
              replacementCheckpoint.targetShopifyVariantId,
            );

  const storeReplacementCheckpoint = <T extends BomStaleReplacementCheckpoint>(
    operation: () => T,
  ): T => {
    try {
      const checkpoint = operation();
      setReplacementCheckpoint(checkpoint);
      setReplacementCheckpointError(null);
      return checkpoint;
    } catch (error) {
      const storageError =
        error instanceof Error
          ? error
          : new Error("BOM replacement checkpoint is unavailable");
      setReplacementCheckpointError(storageError);
      throw storageError;
    }
  };

  const startReplacementCheckpoint = (
    claimToken: string,
    input: Parameters<typeof saveBomStaleReplacementCheckpoint>[2],
  ) =>
    storeReplacementCheckpoint(() =>
      saveBomStaleReplacementCheckpoint(
        shopGeneration.shopGeneration,
        claimToken,
        input,
      ),
    );

  const transitionReplacementCheckpoint = (
    expected: BomStaleReplacementCheckpoint,
    input: Parameters<typeof transitionBomStaleReplacementCheckpoint>[2],
  ) =>
    storeReplacementCheckpoint(() =>
      transitionBomStaleReplacementCheckpoint(
        shopGeneration.shopGeneration,
        expected,
        input,
      ),
    );

  const forgetReplacementCheckpoint = (
    expected: BomStaleReplacementCheckpoint,
  ) => {
    try {
      clearBomStaleReplacementCheckpoint(
        shopGeneration.shopGeneration,
        expected,
      );
      setReplacementCheckpoint(null);
      setReplacementCheckpointError(null);
    } catch (error) {
      const storageError =
        error instanceof Error
          ? error
          : new Error("BOM replacement checkpoint is unavailable");
      setReplacementCheckpointError(storageError);
      throw storageError;
    }
  };

  const deleteStaleConfiguredKit = async (kit: KitBom) => {
    const sourceId = canonicalStockyPublicBigIntId(kit.shopify_variant_id);
    if (
      staleDeleteInFlight.current.has(sourceId) ||
      replacementCheckpointError !== null ||
      (creatingReplacementFor !== null &&
        canonicalStockyPublicBigIntId(creatingReplacementFor) === sourceId)
    ) {
      return;
    }
    const matchingCheckpoint =
      replacementCheckpoint !== null &&
      canonicalStockyPublicBigIntId(
        replacementCheckpoint.sourceKit.shopify_variant_id,
      ) === sourceId
        ? replacementCheckpoint
        : null;
    if (
      matchingCheckpoint !== null &&
      matchingCheckpoint.phase !== "intent-or-sync"
    ) {
      setStaleDeleteErrors((current) => ({
        ...current,
        [sourceId]: t(
          "Shopifyへの反映処理を開始済みです。同じ置換処理を完了してください。",
        ),
      }));
      return;
    }

    // Register before confirm so two clicks in the same React render cannot
    // open two confirms or issue two requests.
    staleDeleteInFlight.current.add(sourceId);
    try {
      const confirmed = window.confirm(
        t(
          "「{name}」のUreyukiBox内の旧セット設定（原材料 {n} 件）だけを削除します。Shopifyの商品と作成済みの下書き商品は削除されず、そのまま残ります。この操作は取り消せません。よろしいですか？",
          {
            name: kit.title ?? kit.sku ?? sourceId,
            n: kit.components.length,
          },
        ),
      );
      if (!confirmed) return;
      setDeletingStaleIds((current) => new Set(current).add(sourceId));
      setStaleDeleteErrors((current) => {
        const next = { ...current };
        delete next[sourceId];
        return next;
      });
      assertCurrentShopGeneration(shopGeneration.shopDomain);
      await deleteStaleKitLocalConfiguration(
        sourceId,
        kit.components.map((component) => ({
          component_shopify_variant_id: canonicalStockyPublicBigIntId(
            component.shopify_variant_id,
          ),
          quantity_per: component.quantity_per,
        })),
        matchingCheckpoint?.workflowId ?? null,
      );
      assertCurrentShopGeneration(shopGeneration.shopDomain);

      // The configured row and its warning remain authoritative until the
      // server has committed and returned the exact bodyless 204 above.
      queryClient.setQueryData<KitBom[]>(KITS_QUERY_KEY, (current) =>
        (current ?? []).filter(
          (configuredKit) =>
            canonicalStockyPublicBigIntId(
              configuredKit.shopify_variant_id,
            ) !== sourceId,
        ),
      );
      if (matchingCheckpoint !== null) {
        try {
          forgetReplacementCheckpoint(matchingCheckpoint);
        } catch {
          // The server cancelled the exact claim atomically with deletion, so
          // it is safe to release this in-memory workflow even if browser
          // storage itself became unavailable. Keeping the failed clear as a
          // page-wide lock would strand every unrelated kit after a successful
          // server delete; a reload can retry cleanup of the now-empty source.
          setReplacementCheckpoint(null);
          setReplacementCheckpointError(null);
        }
      }
      if (
        pendingProductCatalogSync !== null &&
        canonicalStockyPublicBigIntId(
          pendingProductCatalogSync.sourceKit.shopify_variant_id,
        ) === sourceId
      ) {
        setPendingProductCatalogSync(null);
      }
      const workflowTargetId =
        matchingCheckpoint?.phase === "intent-or-sync"
          ? matchingCheckpoint.claimedTargetShopifyVariantId
          : null;
      if (
        replacement !== null &&
        canonicalStockyPublicBigIntId(
          replacement.sourceKit.shopify_variant_id,
        ) === sourceId
      ) {
        setReplacement(null);
      }
      if (
        selectedShopifyVariantId !== null &&
        (canonicalStockyPublicBigIntId(selectedShopifyVariantId) === sourceId ||
          (workflowTargetId !== null &&
            canonicalStockyPublicBigIntId(selectedShopifyVariantId) ===
              canonicalStockyPublicBigIntId(workflowTargetId)))
      ) {
        setSelectedShopifyVariantId(null);
      }
      // A successful delete for an unrelated stale row must not release or
      // overwrite another source's target-write/cleanup recovery state.
      if (replacementCheckpoint === null || matchingCheckpoint !== null) {
        setReplacementNotice({
          tone: "success",
          title: t("旧セット設定をUreyukiBoxから削除しました"),
          message: t(
            "Shopifyの商品と作成済みの下書き商品は削除せず、そのまま残しています。",
          ),
        });
      }
      void kitsQuery.refetch();
    } catch (error) {
      if (error instanceof ShopGenerationChangedError) return;
      setStaleDeleteErrors((current) => ({
        ...current,
        [sourceId]:
          error instanceof Error
            ? error.message
            : t("旧セット設定を削除できませんでした。再試行してください。"),
      }));
    } finally {
      staleDeleteInFlight.current.delete(sourceId);
      try {
        assertCurrentShopGeneration(shopGeneration.shopDomain);
        setDeletingStaleIds((current) => {
          const next = new Set(current);
          next.delete(sourceId);
          return next;
        });
      } catch {
        // This generation is unmounting; never update the next shop's state.
      }
    }
  };

  useEffect(() => {
    if (
      restoredTargetCheckpoint.current ||
      replacementCheckpoint === null ||
      replacementCheckpoint.phase === "intent-or-sync" ||
      catalogQuery.isLoading ||
      catalogQuery.isError
    ) {
      return;
    }
    restoredTargetCheckpoint.current = true;
    const targetId = canonicalStockyPublicBigIntId(
      replacementCheckpoint.targetShopifyVariantId,
    );
    const target = catalog.find(
      (variant) =>
        canonicalStockyPublicBigIntId(variant.shopify_variant_id) === targetId,
    );
    setReplacementCommitLocked(true);
    if (target === undefined) {
      setReplacementNotice({
        tone: "critical",
        title: t("保存先の商品を現在のShopifyで確認できません"),
        message: t("旧商品は整理していません。商品を同期してから、この画面を読み込み直してください。"),
      });
      return;
    }
    setReplacement({
      sourceKit: replacementCheckpoint.sourceKit,
      candidates: [target],
      selectedTargetId: targetId,
      serverBound: true,
    });
    setSelectedShopifyVariantId(targetId);
    setReplacementNotice({
      tone: "info",
      title: t("中断したセット商品の置き換えを再開しました"),
      message:
        replacementCheckpoint.phase === "cleanup"
          ? t("新しい商品への保存は完了しています。Shopifyへ再送せず、旧セットの整理だけを再試行できます。")
          : t("同じ商品と構成だけを使って、安全に保存を再試行できます。"),
    });
    setEditorRevealRequest((request) => request + 1);
  }, [catalog, catalogQuery.isError, catalogQuery.isLoading, replacementCheckpoint]);

  useEffect(() => {
    if (
      replacementCheckpoint === null ||
      !kitsQuery.isSuccess ||
      kitsQuery.isFetching
    ) {
      return;
    }
    const sourceId = canonicalStockyPublicBigIntId(
      replacementCheckpoint.sourceKit.shopify_variant_id,
    );
    const sourceStillConfigured = (kitsQuery.data ?? []).some(
      (kit) =>
        canonicalStockyPublicBigIntId(kit.shopify_variant_id) === sourceId,
    );
    if (sourceStillConfigured) return;

    // A configured-kit query that completed without the exact source proves
    // there is no local BOM left to replace or clean. Retire stale browser
    // evidence so a failed sessionStorage remove after a committed 204 cannot
    // resurrect a page-wide recovery lock on reload. The server claim/receipt
    // remains the durable audit record.
    try {
      clearBomStaleReplacementCheckpoint(
        shopGeneration.shopGeneration,
        replacementCheckpoint,
      );
    } catch {
      // The current verified server snapshot is authoritative for whether a
      // local source exists; retry the exact storage cleanup on the next load.
    }
    setReplacementCheckpoint(null);
    setReplacementCheckpointError(null);
    setPendingProductCatalogSync(null);
    setReplacement(null);
    setReplacementCommitLocked(false);
    setReplacementNotice(null);
  }, [
    kitsQuery.data,
    kitsQuery.isFetching,
    kitsQuery.isSuccess,
    replacementCheckpoint,
    shopGeneration.shopGeneration,
  ]);

  // The editor is rendered above the configured-kit table.  A monotonically
  // increasing request (rather than the selected ID alone) makes clicking the
  // same row again focus and reveal the editor instead of becoming a React
  // state no-op.
  useEffect(() => {
    if (editorRevealRequest === 0 || selectedShopifyVariantId === null) return;
    const editor = editorRef.current;
    if (editor === null) return;
    editor.focus({ preventScroll: true });
    editor.scrollIntoView?.({ behavior: "smooth", block: "start" });
  }, [editorRevealRequest, selectedShopifyVariantId]);

  const editConfiguredKit = (shopifyVariantId: StockyPublicBigIntId) => {
    const requestedId = canonicalStockyPublicBigIntId(shopifyVariantId);
    if (replacementTargetIds.has(requestedId)) {
      returnToExactReplacement(requestedId);
      return;
    }
    if (
      replacementCheckpointError !== null ||
      deletingStaleIds.has(requestedId) ||
      replacementSourceIds.has(requestedId)
    ) {
      return;
    }
    // Keep an unrelated replacement workflow intact. It owns only its exact
    // source/target IDs and must not lock ordinary configured-kit editing.
    setSelectedShopifyVariantId(requestedId);
    setEditorRevealRequest((request) => request + 1);
  };

  const continueProductCatalogSync = async (
    pending: PendingProductCatalogSync,
    workflowCheckpoint: BomStaleReplacementCheckpoint,
  ) => {
    if (productCatalogSyncInFlight.current) return;
    const serverDraft = pending.draftProtocol === "server_product_set_v1";
    if (
      workflowCheckpoint.draftProtocol !== pending.draftProtocol ||
      (serverDraft &&
        (pending.claimedTargetShopifyVariantId === null ||
          pending.targetShopifyProductId === null ||
          !pending.intentCompleted))
    ) {
      setReplacementNotice({
        tone: "critical",
        title: t("商品同期を開始できません"),
        message: t("保存済みの新しい商品を確認できません。商品編集の再開からやり直してください。"),
      });
      return;
    }
    productCatalogSyncInFlight.current = true;
    const sourceId = canonicalStockyPublicBigIntId(
      pending.sourceKit.shopify_variant_id,
    );
    const targetId =
      pending.claimedTargetShopifyVariantId === null
        ? null
        : canonicalStockyPublicBigIntId(
            pending.claimedTargetShopifyVariantId,
          );
    const beforeIds = new Set(pending.beforeVariantIds);
    setCreatingReplacementFor(sourceId);
    try {
      assertCurrentShopGeneration(shopGeneration.shopDomain);
      // A restored checkpoint can render before the initial catalog request
      // settles. Finish that read first so the post-sync refetch cannot be
      // deduplicated onto pre-sync data.
      if (catalogQuery.isFetching) {
        const initialCatalog = await catalogQuery.refetch();
        if (initialCatalog.isError) {
          throw initialCatalog.error instanceof Error
            ? initialCatalog.error
            : new Error(t("現在の商品一覧を確認できませんでした。"));
        }
        assertCurrentShopGeneration(shopGeneration.shopDomain);
      }
      const sync = await triggerSync(shopGeneration.shopId);
      assertCurrentShopGeneration(shopGeneration.shopDomain);
      const responseDomain = canonicalShopDomain(sync.shopify_domain);
      if (
        canonicalStockyPublicBigIntId(sync.shop_id) !== shopGeneration.shopId ||
        responseDomain !== shopGeneration.shopDomain
      ) {
        throw new ShopGenerationChangedError();
      }
      const productStage = sync.runs.find(
        (run) => run.stage === "products_variants",
      );
      if (productStage?.status !== "completed") {
        throw new Error(
          t("商品登録後の商品同期を完了できませんでした。セット構成は変更していません。もう一度お試しください。"),
        );
      }

      const refreshedCatalog = await runInShopGeneration(
        shopGeneration.shopDomain,
        async () => {
          const result = await catalogQuery.refetch();
          if (result.isError || result.data === undefined) {
            throw result.error instanceof Error
              ? result.error
              : new Error("product catalog refresh failed");
          }
          return result.data;
        },
      );
      const candidates = refreshedCatalog.filter((variant) => {
        const variantId = canonicalStockyPublicBigIntId(
          variant.shopify_variant_id,
        );
        if (variantId === sourceId) return false;
        return targetId === null
          ? !beforeIds.has(variantId)
          : variantId === targetId;
      });
      if (
        refreshedCatalog.some(
          (variant) =>
            canonicalStockyPublicBigIntId(variant.shopify_variant_id) ===
            sourceId,
        )
      ) {
        throw new Error(
          t("旧キット商品が現在のShopify商品として再確認されました。新しい商品へ移さず、一覧を読み込み直して編集してください。"),
        );
      }
      if (candidates.length === 0) {
        throw new Error(
          serverDraft
            ? t("商品編集は完了しましたが、保存先の商品を同期一覧で確認できませんでした。セット構成は変更していません。もう一度同期してください。")
            : t("以前の商品登録結果を同期一覧で確認できませんでした。商品を新しく作らず、もう一度同期してください。"),
        );
      }

      assertCurrentShopGeneration(shopGeneration.shopDomain);
      queryClient.setQueryData(VARIANTS_QUERY_KEY, refreshedCatalog);
      if (!serverDraft) {
        const exactTarget = targetId === null ? null : candidates[0] ?? null;
        setReplacement({
          sourceKit: pending.sourceKit,
          candidates,
          selectedTargetId: exactTarget?.shopify_variant_id ?? null,
          serverBound: exactTarget !== null,
        });
        setSelectedShopifyVariantId(exactTarget?.shopify_variant_id ?? null);
        setPendingProductCatalogSync(null);
        setReplacementNotice({
          tone: "info",
          title:
            exactTarget === null
              ? t("以前の商品登録結果を確認しました")
              : t("保存先の商品を確認しました"),
          message:
            exactTarget === null
              ? t("同期後に確認できた候補から、以前に登録した商品を選んでください。新しい商品は作成していません。")
              : t("サーバーで確定済みの同じ商品だけを保存先に使います。商品を作り直しません。"),
        });
        if (exactTarget !== null) {
          setEditorRevealRequest((request) => request + 1);
        }
        return;
      }

      const target = candidates[0]!;
      // Even when every source component is current, stop after the exact
      // server-bound target has been synced. The editor prefills the source
      // BOM, but the merchant must explicitly apply it before the durable
      // target-write + cleanup protocol can begin.
      setReplacement({
        sourceKit: pending.sourceKit,
        candidates: [target],
        selectedTargetId: targetId,
        serverBound: true,
      });
      setSelectedShopifyVariantId(targetId);
      setPendingProductCatalogSync(null);
      setReplacementNotice({
        tone: "info",
        title: t("新しい商品を保存しました"),
        message: pending.allComponentsCurrent
          ? t("セット構成を確認し、「セット構成を保存して在庫へ反映する」を押してください。押すまで旧セットと警告は残ります。")
          : t("現在のShopifyにない原材料を選び直し、「セット構成を保存して在庫へ反映する」を押してください。押すまで旧セットと警告は残ります。"),
      });
      setEditorRevealRequest((request) => request + 1);
    } catch (error) {
      // A tenant transition unmounts this generation. Never publish its late
      // result or an error into the next shop's UI.
      if (error instanceof ShopGenerationChangedError) return;
      setReplacementNotice({
        tone: "critical",
        title: t("商品登録後の同期を続けられませんでした"),
        message:
          error instanceof Error
            ? error.message
            : t("新しい商品はShopifyに保存済みです。商品を作り直さず、同期だけを再試行してください。"),
      });
    } finally {
      productCatalogSyncInFlight.current = false;
      try {
        assertCurrentShopGeneration(shopGeneration.shopDomain);
        setCreatingReplacementFor(null);
      } catch {
        // The verified generation owns this state and is already being
        // unmounted. Do not write through to a different shop.
      }
    }
  };

  const prepareAndEditReplacement = async (
    pending: PendingProductCatalogSync,
    expectedCheckpoint: BomStaleReplacementCheckpoint,
  ) => {
    if (productEditIntentInFlight.current) return;
    if (
      pending.draftProtocol !== "server_product_set_v1" ||
      expectedCheckpoint.draftProtocol !== "server_product_set_v1"
    ) {
      setReplacementNotice({
        tone: "critical",
        title: t("以前の商品登録処理は商品同期だけで再開してください"),
        message: t("重複商品を防ぐため、以前の処理には下書き商品の作成を実行しません。"),
      });
      return;
    }
    productEditIntentInFlight.current = true;
    const sourceId = canonicalStockyPublicBigIntId(
      pending.sourceKit.shopify_variant_id,
    );
    setCreatingReplacementFor(sourceId);
    let readyPending = pending;
    try {
      assertCurrentShopGeneration(shopGeneration.shopDomain);
      const draft = await prepareStaleKitReplacementDraft(
        sourceId,
        expectedCheckpoint.workflowId,
      );
      assertCurrentShopGeneration(shopGeneration.shopDomain);
      if (draft.claim_token !== expectedCheckpoint.workflowId) {
        throw new Error("replacement draft claim changed");
      }
      if (
        canonicalStockyPublicBigIntId(
          draft.source_shopify_variant_id,
        ) !== sourceId
      ) {
        throw new Error("replacement draft source changed");
      }
      if (draft.draft_protocol !== "server_product_set_v1") {
        throw new Error("replacement draft protocol changed");
      }
      if (draft.draft_state !== "ready") {
        setPendingProductCatalogSync(pending);
        setReplacementNotice({
          tone: "info",
          title: t("Shopify商品の作成結果を確認しています"),
          message: t("重複商品を作らず、同じ処理の結果だけを再確認します。少し待ってから再試行してください。"),
        });
        return;
      }
      if (
        draft.target_shopify_variant_id === null ||
        draft.shopify_product_id === null
      ) {
        throw new Error("replacement draft target is unavailable");
      }
      const targetId = canonicalStockyPublicBigIntId(
        draft.target_shopify_variant_id,
      );
      if (
        expectedCheckpoint.phase === "intent-or-sync" &&
        expectedCheckpoint.claimedTargetShopifyVariantId !== null &&
        canonicalStockyPublicBigIntId(
          expectedCheckpoint.claimedTargetShopifyVariantId,
        ) !== targetId
      ) {
        throw new Error("replacement draft target changed");
      }
      readyPending = {
        ...pending,
        claimedTargetShopifyVariantId: targetId,
        targetShopifyProductId: canonicalStockyPublicBigIntId(
          draft.shopify_product_id,
        ),
        allComponentsCurrent: draft.all_components_current,
        components: draft.components,
      };
      setPendingProductCatalogSync(readyPending);

      if (readyPending.intentCompleted) {
        await continueProductCatalogSync(readyPending, expectedCheckpoint);
        return;
      }
      const outcome = await editShopifyProductWithAdminIntent(
        canonicalStockyPublicBigIntId(draft.shopify_product_id),
      );
      assertCurrentShopGeneration(shopGeneration.shopDomain);
      if (outcome === "closed") {
        setPendingProductCatalogSync({
          ...readyPending,
          editorClosed: true,
        });
        setReplacementNotice({
          tone: "info",
          title: t("Shopifyの商品編集を閉じました"),
          message: t("Shopifyで保存済みの場合は保存確認から商品同期へ進めます。保存していない場合は、同じ下書き商品の編集を再開してください。"),
        });
        return;
      }

      if (
        expectedCheckpoint.phase !== "intent-or-sync" ||
        expectedCheckpoint.intentCompleted
      ) {
        throw new BomStaleReplacementCheckpointStorageError(
          "replacement edit checkpoint is invalid",
        );
      }
      const completedCheckpoint = transitionReplacementCheckpoint(
        expectedCheckpoint,
        {
          phase: "intent-or-sync",
          draftProtocol: "server_product_set_v1",
          intentCompleted: true,
          claimedTargetShopifyVariantId:
            expectedCheckpoint.claimedTargetShopifyVariantId,
          sourceKit: readyPending.sourceKit,
          beforeVariantIds: readyPending.beforeVariantIds,
        },
      );
      const completedPending = { ...readyPending, intentCompleted: true };
      setPendingProductCatalogSync(completedPending);
      if (completedCheckpoint.phase !== "intent-or-sync") {
        throw new BomStaleReplacementCheckpointStorageError(
          "replacement edit checkpoint transition is invalid",
        );
      }
      await continueProductCatalogSync(
        completedPending,
        completedCheckpoint,
      );
    } catch (error) {
      if (error instanceof ShopGenerationChangedError) return;
      setPendingProductCatalogSync(readyPending);
      const unavailable =
        error instanceof ShopifyProductEditIntentError &&
        error.code === "unavailable";
      setReplacementNotice({
        tone: "critical",
        title: t("商品登録を続けられませんでした"),
        message: unavailable
          ? t("Shopifyの商品編集画面を開けませんでした。Shopify管理画面からUreyukiBoxを開き直してください。")
          : error instanceof Error
            ? error.message
            : t("Shopifyの商品編集を完了できませんでした。旧セットは変更していません。"),
      });
    } finally {
      productEditIntentInFlight.current = false;
      try {
        assertCurrentShopGeneration(shopGeneration.shopDomain);
        setCreatingReplacementFor(null);
      } catch {
        // This generation is being unmounted; never update another shop.
      }
    }
  };

  const confirmReplacementProductSaved = async (
    pending: PendingProductCatalogSync,
  ) => {
    if (
      productSaveConfirmationInFlight.current ||
      productEditIntentInFlight.current ||
      productCatalogSyncInFlight.current
    ) {
      return;
    }
    if (
      !window.confirm(
        t("同じShopify商品をUreyukiBoxの商品一覧へ読み込みます。旧キット、在庫数量、セット構成はまだ変更しません。次の画面で「セット構成を保存して在庫へ反映する」を押す必要があります。よろしいですか？"),
      )
    ) {
      return;
    }
    productSaveConfirmationInFlight.current = true;
    const sourceId = canonicalStockyPublicBigIntId(
      pending.sourceKit.shopify_variant_id,
    );
    setCreatingReplacementFor(sourceId);
    try {
      assertCurrentShopGeneration(shopGeneration.shopDomain);
      const checkpoint = replacementCheckpoint;
      const targetId =
        pending.claimedTargetShopifyVariantId === null
          ? null
          : canonicalStockyPublicBigIntId(
              pending.claimedTargetShopifyVariantId,
            );
      if (
        pending.draftProtocol !== "server_product_set_v1" ||
        pending.intentCompleted ||
        !pending.editorClosed ||
        pending.targetShopifyProductId === null ||
        targetId === null ||
        checkpoint === null ||
        checkpoint.phase !== "intent-or-sync" ||
        checkpoint.draftProtocol !== "server_product_set_v1" ||
        checkpoint.intentCompleted ||
        canonicalStockyPublicBigIntId(
          checkpoint.sourceKit.shopify_variant_id,
        ) !== sourceId ||
        (checkpoint.claimedTargetShopifyVariantId !== null &&
          canonicalStockyPublicBigIntId(
            checkpoint.claimedTargetShopifyVariantId,
          ) !== targetId)
      ) {
        throw new Error("replacement save confirmation checkpoint changed");
      }

      // Re-read the exact durable claim immediately before advancing the
      // browser checkpoint. Closing Admin Intent is never treated as proof of
      // save, and a stale callback cannot authorize a different source/claim.
      const draft = await prepareStaleKitReplacementDraft(
        sourceId,
        checkpoint.workflowId,
      );
      assertCurrentShopGeneration(shopGeneration.shopDomain);
      if (
        draft.claim_token !== checkpoint.workflowId ||
        canonicalStockyPublicBigIntId(
          draft.source_shopify_variant_id,
        ) !== sourceId ||
        draft.draft_protocol !== "server_product_set_v1" ||
        draft.draft_state !== "ready" ||
        draft.shopify_product_id === null ||
        canonicalStockyPublicBigIntId(draft.shopify_product_id) !==
          pending.targetShopifyProductId ||
        draft.target_shopify_variant_id === null ||
        canonicalStockyPublicBigIntId(
          draft.target_shopify_variant_id,
        ) !== targetId ||
        draft.all_components_current !== pending.allComponentsCurrent ||
        JSON.stringify(draft.components) !== JSON.stringify(pending.components)
      ) {
        throw new Error("replacement save confirmation claim changed");
      }

      const completedCheckpoint = transitionReplacementCheckpoint(
        checkpoint,
        {
          phase: "intent-or-sync",
          draftProtocol: "server_product_set_v1",
          intentCompleted: true,
          claimedTargetShopifyVariantId:
            checkpoint.claimedTargetShopifyVariantId,
          sourceKit: pending.sourceKit,
          beforeVariantIds: pending.beforeVariantIds,
        },
      );
      if (completedCheckpoint.phase !== "intent-or-sync") {
        throw new BomStaleReplacementCheckpointStorageError(
          "replacement save confirmation transition is invalid",
        );
      }
      const completedPending = { ...pending, intentCompleted: true };
      setPendingProductCatalogSync(completedPending);
      await continueProductCatalogSync(
        completedPending,
        completedCheckpoint,
      );
    } catch (error) {
      if (error instanceof ShopGenerationChangedError) return;
      setPendingProductCatalogSync(pending);
      setReplacementNotice({
        tone: "critical",
        title: t("商品同期を開始できません"),
        message: t("保存済みの新しい商品を確認できません。商品編集の再開からやり直してください。"),
      });
    } finally {
      productSaveConfirmationInFlight.current = false;
      try {
        assertCurrentShopGeneration(shopGeneration.shopDomain);
        setCreatingReplacementFor(null);
      } catch {
        // The verified generation owns this state and is already unmounting.
      }
    }
  };

  const resumePendingReplacement = async (
    pending: PendingProductCatalogSync,
  ) => {
    const sourceId = canonicalStockyPublicBigIntId(
      pending.sourceKit.shopify_variant_id,
    );
    // The merchant has confirmed local deletion and the exact server claim is
    // being cancelled. Never start a late sync/edit continuation for it.
    if (staleDeleteInFlight.current.has(sourceId)) return;
    const checkpoint = replacementCheckpoint;
    if (checkpoint === null || checkpoint.phase !== "intent-or-sync") {
      setReplacementNotice({
        tone: "critical",
        title: t("安全な再開情報を確認できません"),
        message: t("旧セットは変更していません。画面を読み込み直してください。"),
      });
      return;
    }
    if (checkpoint.draftProtocol === "legacy_native_create") {
      if (
        legacyReconcileInFlight.current ||
        productCatalogSyncInFlight.current
      ) return;
      legacyReconcileInFlight.current = true;
      setCreatingReplacementFor(sourceId);
      try {
        assertCurrentShopGeneration(shopGeneration.shopDomain);
        const claim = await acquireStaleKitReplacementClaim(sourceId);
        assertCurrentShopGeneration(shopGeneration.shopDomain);
        if (
          claim.claim_token !== checkpoint.workflowId ||
          claim.draft_protocol !== "legacy_native_create"
        ) {
          throw new Error(
            t("以前の商品登録処理の再開情報がサーバーと一致しません。旧セットは変更していません。"),
          );
        }
        const targetId =
          claim.target_shopify_variant_id === null
            ? null
            : canonicalStockyPublicBigIntId(
                claim.target_shopify_variant_id,
              );
        let reconciledCheckpoint = checkpoint;
        if (
          targetId !== null &&
          checkpoint.claimedTargetShopifyVariantId === null
        ) {
          const transitioned = transitionReplacementCheckpoint(checkpoint, {
            phase: "intent-or-sync",
            draftProtocol: "legacy_native_create",
            intentCompleted: true,
            claimedTargetShopifyVariantId: targetId,
            sourceKit: pending.sourceKit,
            beforeVariantIds: pending.beforeVariantIds,
          });
          if (transitioned.phase !== "intent-or-sync") {
            throw new BomStaleReplacementCheckpointStorageError(
              "legacy checkpoint transition is invalid",
            );
          }
          reconciledCheckpoint = transitioned;
        } else if (
          targetId !== checkpoint.claimedTargetShopifyVariantId
        ) {
          throw new Error(
            t("以前の商品登録処理の保存先が一致しません。旧セットは変更していません。"),
          );
        }
        const reconciledPending: PendingProductCatalogSync = {
          ...pending,
          claimedTargetShopifyVariantId: targetId,
          intentCompleted:
            targetId === null ? pending.intentCompleted : true,
        };
        setPendingProductCatalogSync(reconciledPending);
        await continueProductCatalogSync(
          reconciledPending,
          reconciledCheckpoint,
        );
      } catch (error) {
        if (error instanceof ShopGenerationChangedError) return;
        setReplacementNotice({
          tone: "critical",
          title: t("以前の商品登録処理を再開できませんでした"),
          message:
            error instanceof Error
              ? error.message
              : t("商品を新しく作らず安全に停止しました。同じ処理を再試行してください。"),
        });
      } finally {
        legacyReconcileInFlight.current = false;
        try {
          assertCurrentShopGeneration(shopGeneration.shopDomain);
          setCreatingReplacementFor(null);
        } catch {
          // This generation is being unmounted; never update another shop.
        }
      }
      return;
    }
    await prepareAndEditReplacement(pending, checkpoint);
  };

  const returnToExactReplacement = (
    requestedId: StockyPublicBigIntId,
  ): boolean => {
    const canonicalRequestedId = canonicalStockyPublicBigIntId(requestedId);
    if (
      exactRecoveryTargetId === null ||
      canonicalRequestedId !== exactRecoveryTargetId
    ) {
      return false;
    }
    if (
      replacement !== null &&
      replacement.selectedTargetId !== null &&
      canonicalStockyPublicBigIntId(replacement.selectedTargetId) ===
        canonicalRequestedId
    ) {
      setSelectedShopifyVariantId(canonicalRequestedId);
      setEditorRevealRequest((request) => request + 1);
      return true;
    }
    if (pendingProductCatalogSync !== null) {
      void resumePendingReplacement(pendingProductCatalogSync);
      return true;
    }
    return false;
  };

  const createReplacementProduct = async (sourceKit: KitBom) => {
    if (
      replacementStartInFlight.current ||
      pendingProductCatalogSync !== null ||
      replacement !== null ||
      replacementCheckpoint !== null ||
      replacementCheckpointError !== null
    ) {
      return;
    }
    replacementStartInFlight.current = true;
    const sourceId = canonicalStockyPublicBigIntId(
      sourceKit.shopify_variant_id,
    );
    setCreatingReplacementFor(sourceId);
    setReplacement(null);
    setReplacementNotice(null);
    setSelectedShopifyVariantId(null);
    let claimToken: string | null = null;
    let claimCanBeCancelled = false;
    try {
      assertCurrentShopGeneration(shopGeneration.shopDomain);
      await ensureShopifyOptionalScope("write_products");
      assertCurrentShopGeneration(shopGeneration.shopDomain);
      const claim = await acquireStaleKitReplacementClaim(sourceId);
      assertCurrentShopGeneration(shopGeneration.shopDomain);
      claimToken = claim.claim_token;
      claimCanBeCancelled =
        claim.draft_protocol === "server_product_set_v1" &&
        claim.should_create_product &&
        claim.draft_state === "not_started" &&
        claim.target_shopify_variant_id === null;
      const targetId =
        claim.target_shopify_variant_id === null
          ? null
          : canonicalStockyPublicBigIntId(claim.target_shopify_variant_id);
      const beforeVariantIds = catalog
        .map((variant) =>
          canonicalStockyPublicBigIntId(variant.shopify_variant_id),
        )
        .filter((variantId) => variantId !== targetId);
      const recoveryBeforeVariantIds =
        claim.draft_protocol === "legacy_native_create" &&
        !claim.should_create_product
          ? []
          : beforeVariantIds;
      const pending: PendingProductCatalogSync = {
        sourceKit,
        beforeVariantIds: recoveryBeforeVariantIds,
        draftProtocol: claim.draft_protocol,
        claimedTargetShopifyVariantId: targetId,
        targetShopifyProductId:
          claim.shopify_product_id === null
            ? null
            : canonicalStockyPublicBigIntId(claim.shopify_product_id),
        intentCompleted: false,
        editorClosed: false,
        allComponentsCurrent: false,
        components: [],
      };
      const checkpoint = startReplacementCheckpoint(claim.claim_token, {
        phase: "intent-or-sync",
        draftProtocol: claim.draft_protocol,
        intentCompleted: false,
        claimedTargetShopifyVariantId: targetId,
        sourceKit,
        beforeVariantIds: recoveryBeforeVariantIds,
      });
      setPendingProductCatalogSync(pending);
      if (claim.draft_protocol === "legacy_native_create") {
        setReplacementNotice({
          tone: "info",
          title: t("以前の商品登録処理を安全に再確認します"),
          message: t("重複商品を防ぐため、新しい商品は作らず商品同期だけを実行します。"),
        });
        await continueProductCatalogSync(pending, checkpoint);
        return;
      }
      await prepareAndEditReplacement(pending, checkpoint);
    } catch (error) {
      if (error instanceof ShopGenerationChangedError) return;
      if (
        error instanceof BomStaleReplacementCheckpointStorageError &&
        claimCanBeCancelled &&
        claimToken !== null
      ) {
        try {
          await cancelStaleKitReplacementClaim(sourceId, claimToken);
        } catch {
          // No Shopify draft request was issued. Leave the server claim as the
          // only authority if its exact cancellation cannot be confirmed.
        }
      }
      setReplacementNotice({
        tone: "critical",
        title: t("商品登録を続けられませんでした"),
        message:
          error instanceof BomStaleReplacementCheckpointStorageError
            ? t("安全な再開情報をブラウザに保存できないため、商品登録を開始しませんでした。ブラウザの保存設定を確認して画面を読み込み直してください。")
            : error instanceof ShopifyOptionalScopeError
              ? error.message
              : error instanceof Error
                ? error.message
                : t("Shopifyの商品登録を開始できませんでした。旧セットは変更していません。"),
      });
    } finally {
      replacementStartInFlight.current = false;
      try {
        assertCurrentShopGeneration(shopGeneration.shopDomain);
        setCreatingReplacementFor(null);
      } catch {
        // This generation is being unmounted; never update another shop.
      }
    }
  };

  const chooseReplacementTarget = (value: string) => {
    if (
      replacement === null ||
      replacement.serverBound ||
      replacementCommitLocked
    ) return;
    const targetId =
      value === "" ? null : canonicalStockyPublicBigIntId(value);
    setReplacement({ ...replacement, selectedTargetId: targetId });
    setSelectedShopifyVariantId(targetId);
    if (targetId !== null) {
      setEditorRevealRequest((request) => request + 1);
    }
  };

  const finishReplacement = (targetId: StockyPublicBigIntId) => {
    const sourceId = replacement?.sourceKit.shopify_variant_id ?? null;
    setReplacementCommitLocked(false);
    setReplacement(null);
    setPendingProductCatalogSync(null);
    setReplacementNotice({
      tone: "success",
      title: t("新しいShopify商品へセット構成を移しました"),
      message: t("旧商品のセット構成は整理され、現在の商品だけが残っています。"),
    });
    setSelectedShopifyVariantId(
      canonicalStockyPublicBigIntId(targetId),
    );
    if (sourceId !== null) {
      queryClient.setQueryData<KitBom[]>(KITS_QUERY_KEY, (current) =>
        current?.filter(
          (kit) =>
            canonicalStockyPublicBigIntId(kit.shopify_variant_id) !==
            canonicalStockyPublicBigIntId(sourceId),
        ) ?? [],
      );
    }
    void kitsQuery.refetch();
    void catalogQuery.refetch();
  };

  const checkpointTargetWrite = (
    sourceKit: KitBom,
    targetId: StockyPublicBigIntId,
    components: BomComponentInput[],
    expectedCheckpoint: BomStaleReplacementCheckpoint | null =
      replacementCheckpoint,
  ): BomTargetWriteCheckpoint => {
    const checkpoint = expectedCheckpoint;
    if (
      checkpoint === null ||
      canonicalStockyPublicBigIntId(checkpoint.sourceKit.shopify_variant_id) !==
        canonicalStockyPublicBigIntId(sourceKit.shopify_variant_id)
    ) {
      const error = new BomStaleReplacementCheckpointStorageError(
        "replacement source checkpoint is unavailable",
      );
      setReplacementCheckpointError(error);
      throw error;
    }
    const targetCheckpoint = transitionReplacementCheckpoint(checkpoint, {
      phase: "target-write",
      sourceKit,
      beforeVariantIds: checkpoint.beforeVariantIds,
      targetShopifyVariantId: targetId,
      components,
    });
    setReplacementCommitLocked(true);
    if (targetCheckpoint.phase !== "target-write") {
      throw new BomStaleReplacementCheckpointStorageError(
        "target write checkpoint transition is invalid",
      );
    }
    return targetCheckpoint;
  };

  const checkpointTargetSaved = (
    expected: BomTargetWriteCheckpoint,
    sourceKit: KitBom,
    targetId: StockyPublicBigIntId,
    components: BomComponentInput[],
  ): BomCleanupCheckpoint => {
    const checkpoint = expected;
    if (
      checkpoint === null ||
      canonicalStockyPublicBigIntId(checkpoint.sourceKit.shopify_variant_id) !==
        canonicalStockyPublicBigIntId(sourceKit.shopify_variant_id)
    ) {
      const error = new BomStaleReplacementCheckpointStorageError(
        "replacement source checkpoint is unavailable",
      );
      setReplacementCheckpointError(error);
      throw error;
    }
    const cleanupCheckpoint = transitionReplacementCheckpoint(checkpoint, {
      phase: "cleanup",
      sourceKit,
      beforeVariantIds: checkpoint.beforeVariantIds,
      targetShopifyVariantId: targetId,
      components,
    });
    if (cleanupCheckpoint.phase !== "cleanup") {
      throw new BomStaleReplacementCheckpointStorageError(
        "cleanup checkpoint transition is invalid",
      );
    }
    return cleanupCheckpoint;
  };

  const checkpointCleanupCompleted = (expected: BomCleanupCheckpoint) => {
    forgetReplacementCheckpoint(expected);
  };

  const activeTargetCheckpoint: BomTargetCheckpoint | undefined =
    replacementCheckpoint !== null &&
    replacementCheckpoint.phase !== "intent-or-sync" &&
    selectedShopifyVariantId !== null &&
    canonicalStockyPublicBigIntId(
      replacementCheckpoint.targetShopifyVariantId,
    ) === canonicalStockyPublicBigIntId(selectedShopifyVariantId)
      ? replacementCheckpoint
      : undefined;
  const kitSelectionLocked =
    catalogQuery.isLoading ||
    replacementCheckpointError !== null;
  const selectKit = (value: string) => {
    // A checkpoint read failure means a prior replacement may exist but its
    // phase is unknowable. Keep every bundle writer closed, even if a stale
    // DOM callback is dispatched while the visible select is disabled.
    if (kitSelectionLocked) return;
    if (value === "") {
      setSelectedShopifyVariantId(null);
      return;
    }
    const requestedId = canonicalStockyPublicBigIntId(value);
    if (replacementTargetIds.has(requestedId)) {
      returnToExactReplacement(requestedId);
      return;
    }
    if (
      deletingStaleIds.has(requestedId) ||
      replacementSourceIds.has(requestedId)
    ) {
      return;
    }
    setSelectedShopifyVariantId(requestedId);
  };

  return (
    <Page
      title={t(FEATURE_NAME)}
      subtitle={t("原材料と数量を登録してShopify商品へ反映すると、セット商品の販売可能数が原材料の在庫から自動計算されます")}
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            <Card>
              <BlockStack gap="200">
                <Text as="h2" variant="headingMd">
                  {t("キットを選択")}
                </Text>
                <Box maxWidth="420px">
                  <Select
                    label={t("キット（組立品）")}
                    labelHidden
                    options={kitOptions}
                    value={
                      selectedShopifyVariantId !== null
                        ? canonicalStockyPublicBigIntId(
                            selectedShopifyVariantId,
                          )
                        : ""
                    }
                    onChange={selectKit}
                    disabled={kitSelectionLocked}
                  />
                </Box>
              </BlockStack>
            </Card>

            {replacementNotice !== null && (
              <Banner
                tone={replacementNotice.tone}
                title={replacementNotice.title}
              >
                <p>{replacementNotice.message}</p>
              </Banner>
            )}

            {replacementCheckpointError !== null && (
              <Banner
                tone="critical"
                title={t("安全な再開情報をブラウザへ保存できません")}
              >
                <p>
                  {t("重複作成を防ぐため、この画面では新しい商品を作成しません。ブラウザの保存設定を確認してから読み込み直してください。")}
                </p>
              </Banner>
            )}

            {pendingProductCatalogSync !== null && (
              <Banner
                tone="warning"
                title={
                  pendingProductCatalogSync.draftProtocol ===
                  "legacy_native_create"
                    ? t("以前の商品登録処理を安全に再確認します")
                    : pendingProductCatalogSync.intentCompleted
                    ? t("新しい商品はShopifyに保存済みです")
                    : pendingProductCatalogSync.targetShopifyProductId !== null
                      ? t("Shopifyの商品編集を再開できます")
                      : t("Shopify商品の作成結果を安全に再確認します")
                }
              >
                <BlockStack gap="200">
                  <p>
                    {pendingProductCatalogSync.draftProtocol ===
                    "legacy_native_create"
                      ? t("重複商品を防ぐため、新しい商品は作らず商品同期だけを実行します。")
                      : pendingProductCatalogSync.intentCompleted
                      ? t("同じ保存先だけを同期し、セット構成の保存を再開します。商品を作り直しません。")
                      : pendingProductCatalogSync.targetShopifyProductId !== null
                        ? t("商品名とSKUを入れた同じ下書きを開きます。閉じても別の商品は作りません。")
                        : t("同じ処理を再確認します。通信が途切れても別の商品は作りません。")}
                  </p>
                  {pendingProductCatalogSync.draftProtocol ===
                    "server_product_set_v1" &&
                    pendingProductCatalogSync.targetShopifyProductId !==
                      null && (
                      <p>
                        {t(
                          "商品のステータスは下書きのまま保存してください。アクティブにする必要はなく、UreyukiBoxも販売状態へ変更しません。",
                        )}
                      </p>
                    )}
                  <InlineStack gap="200">
                    <Button
                      loading={creatingReplacementFor !== null}
                      disabled={deletingStaleIds.has(
                        canonicalStockyPublicBigIntId(
                          pendingProductCatalogSync.sourceKit
                            .shopify_variant_id,
                        ),
                      )}
                      onClick={() => {
                        void resumePendingReplacement(
                          pendingProductCatalogSync,
                        );
                      }}
                    >
                      {pendingProductCatalogSync.draftProtocol ===
                      "legacy_native_create"
                        ? t("商品同期だけを再試行")
                        : pendingProductCatalogSync.intentCompleted
                        ? t("商品同期だけを再試行")
                        : pendingProductCatalogSync.targetShopifyProductId !== null
                          ? t("商品編集を再開")
                          : t("作成結果を再確認")}
                    </Button>
                    {pendingProductCatalogSync.draftProtocol ===
                      "server_product_set_v1" &&
                      pendingProductCatalogSync.targetShopifyProductId !==
                        null &&
                      pendingProductCatalogSync.editorClosed &&
                      !pendingProductCatalogSync.intentCompleted && (
                        <Button
                          variant="primary"
                          loading={creatingReplacementFor !== null}
                          disabled={deletingStaleIds.has(
                            canonicalStockyPublicBigIntId(
                              pendingProductCatalogSync.sourceKit
                                .shopify_variant_id,
                            ),
                          )}
                          onClick={() => {
                            void confirmReplacementProductSaved(
                              pendingProductCatalogSync,
                            );
                          }}
                        >
                          {t("Shopifyでの保存を確認して商品同期へ進む")}
                        </Button>
                      )}
                    {exactRecoveryTargetId !== null &&
                      (selectedShopifyVariantId === null ||
                        canonicalStockyPublicBigIntId(
                          selectedShopifyVariantId,
                        ) !== exactRecoveryTargetId) && (
                        <Button
                          disabled={deletingStaleIds.has(
                            canonicalStockyPublicBigIntId(
                              pendingProductCatalogSync.sourceKit
                                .shopify_variant_id,
                            ),
                          )}
                          onClick={() => {
                            returnToExactReplacement(
                              exactRecoveryTargetId,
                            );
                          }}
                        >
                          {t("置換中の商品へ戻る")}
                        </Button>
                      )}
                  </InlineStack>
                </BlockStack>
              </Banner>
            )}

            {replacement !== null && (
              <Card>
                <BlockStack gap="300">
                  <Text as="h2" variant="headingMd">
                    {replacement.serverBound
                      ? t("作成したキット商品")
                      : t("登録したキット商品を選択")}
                  </Text>
                  <Text as="p" tone="subdued">
                    {replacement.serverBound
                      ? t("サーバーが同じストアで作成・確認した商品だけを保存先に固定しています。旧商品IDはShopifyへ送りません。")
                      : t("Shopifyから返された名前やIDは使いません。同期後に新しく確認できた商品から、登録した商品を選んでください。")}
                  </Text>
                  <Box maxWidth="520px">
                    <Select
                      label={t("新しいキット商品")}
                      options={[
                        { label: t("選択してください"), value: "" },
                        ...replacement.candidates.map((candidate) => ({
                          label: variantLabel(candidate),
                          value: canonicalStockyPublicBigIntId(
                            candidate.shopify_variant_id,
                          ),
                        })),
                      ]}
                      value={
                        replacement.selectedTargetId === null
                          ? ""
                          : canonicalStockyPublicBigIntId(
                              replacement.selectedTargetId,
                            )
                      }
                      onChange={chooseReplacementTarget}
                      disabled={
                        replacement.serverBound || replacementCommitLocked
                      }
                    />
                  </Box>
                  {!replacement.serverBound && (
                    <InlineStack gap="200">
                      <Button
                        disabled={
                          replacementCommitLocked ||
                          replacement.selectedTargetId === null
                        }
                        onClick={() => {
                          setReplacement({
                            ...replacement,
                            selectedTargetId: null,
                          });
                          setSelectedShopifyVariantId(null);
                        }}
                      >
                        {t("商品の選択を解除")}
                      </Button>
                    </InlineStack>
                  )}
                  {exactRecoveryTargetId !== null &&
                    (selectedShopifyVariantId === null ||
                      canonicalStockyPublicBigIntId(
                        selectedShopifyVariantId,
                      ) !== exactRecoveryTargetId) && (
                      <InlineStack gap="200">
                        <Button
                          onClick={() => {
                            setSelectedShopifyVariantId(
                              exactRecoveryTargetId,
                            );
                            setEditorRevealRequest(
                              (request) => request + 1,
                            );
                          }}
                        >
                          {t("置換中の商品へ戻る")}
                        </Button>
                      </InlineStack>
                    )}
                </BlockStack>
              </Card>
            )}

            {selectedShopifyVariantId !== null && (
              <div
                ref={editorRef}
                role="region"
                aria-label={t("キット構成の編集")}
                tabIndex={-1}
              >
                <KitEditorCard
                  key={selectedShopifyVariantId}
                  shopifyVariantId={selectedShopifyVariantId}
                  catalog={catalog.filter(
                    (v) =>
                      canonicalStockyPublicBigIntId(v.shopify_variant_id) !==
                      selectedShopifyVariantId,
                  )}
                  parentExistsInShopify={currentShopifyVariantIds.has(
                    canonicalStockyPublicBigIntId(selectedShopifyVariantId),
                  )}
                  catalogIsReliable={!catalogQuery.isError}
                  replacementSource={
                    replacement?.selectedTargetId !== null &&
                    replacement?.selectedTargetId !== undefined &&
                    canonicalStockyPublicBigIntId(
                      replacement.selectedTargetId,
                    ) ===
                      canonicalStockyPublicBigIntId(
                        selectedShopifyVariantId,
                      )
                      ? replacement.sourceKit
                      : undefined
                  }
                  replacementCheckpoint={activeTargetCheckpoint}
                  onReplacementTargetWrite={checkpointTargetWrite}
                  onReplacementTargetSaved={checkpointTargetSaved}
                  onReplacementCleanupCompleted={
                    checkpointCleanupCompleted
                  }
                  onReplacementCommitLockChange={
                    setReplacementCommitLocked
                  }
                  onReplacementComplete={finishReplacement}
                />
              </div>
            )}

            <Text as="h2" variant="headingMd">
              {t("設定済みのキット一覧")}
            </Text>
            <KitsOverviewCard
              kits={kitsQuery.data ?? []}
              currentShopifyVariantIds={currentShopifyVariantIds}
              isLoading={kitsQuery.isLoading || catalogQuery.isLoading}
              isError={kitsQuery.isError || catalogQuery.isError}
              hasCatalog={catalog.length > 0}
              onRetry={() => {
                void kitsQuery.refetch();
                void catalogQuery.refetch();
              }}
              onEdit={editConfiguredKit}
              onCreateReplacement={(kit) => {
                void createReplacementProduct(kit);
              }}
              onDeleteStale={(kit) => {
                void deleteStaleConfiguredKit(kit);
              }}
              creatingReplacementFor={creatingReplacementFor}
              replacementSourceIds={replacementSourceIds}
              replacementWorkflowActive={
                pendingProductCatalogSync !== null ||
                replacement !== null ||
                replacementCommitLocked ||
                replacementCheckpoint !== null
              }
              checkpointUnreadable={replacementCheckpointError !== null}
              deletionBlockedSourceIds={
                replacementCheckpoint !== null &&
                replacementCheckpoint.phase !== "intent-or-sync"
                  ? new Set([
                      canonicalStockyPublicBigIntId(
                        replacementCheckpoint.sourceKit.shopify_variant_id,
                      ),
                    ])
                  : new Set()
              }
              deletingStaleIds={deletingStaleIds}
              staleDeleteErrors={staleDeleteErrors}
            />
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

interface KitEditorCardProps {
  shopifyVariantId: StockyPublicBigIntId;
  catalog: BomVariantOption[];
  parentExistsInShopify: boolean;
  catalogIsReliable: boolean;
  replacementSource?: KitBom;
  replacementCheckpoint?: BomTargetCheckpoint;
  onReplacementTargetWrite: (
    sourceKit: KitBom,
    targetId: StockyPublicBigIntId,
    components: BomComponentInput[],
  ) => BomTargetWriteCheckpoint;
  onReplacementTargetSaved: (
    expected: BomTargetWriteCheckpoint,
    sourceKit: KitBom,
    targetId: StockyPublicBigIntId,
    components: BomComponentInput[],
  ) => BomCleanupCheckpoint;
  onReplacementCleanupCompleted: (expected: BomCleanupCheckpoint) => void;
  onReplacementCommitLockChange: (locked: boolean) => void;
  onReplacementComplete: (targetId: StockyPublicBigIntId) => void;
}

function KitEditorCard({
  shopifyVariantId,
  catalog,
  parentExistsInShopify,
  catalogIsReliable,
  replacementSource,
  replacementCheckpoint,
  onReplacementTargetWrite,
  onReplacementTargetSaved,
  onReplacementCleanupCompleted,
  onReplacementCommitLockChange,
  onReplacementComplete,
}: KitEditorCardProps) {
  const canonicalShopifyVariantId =
    canonicalStockyPublicBigIntId(shopifyVariantId);
  const kitQuery = useQuery({
    queryKey: ["bom-kit", canonicalShopifyVariantId],
    queryFn: () => getKitBom(canonicalShopifyVariantId),
  });

  return (
    <Card>
      <BlockStack gap="300">
        <Text as="h2" variant="headingMd">
          {kitQuery.data?.title ?? t("構成")}
        </Text>
        {kitQuery.isLoading && <SkeletonBodyText lines={4} />}
        {kitQuery.isError && (
          <GatedQueryError
            title={t("構成の取得に失敗しました")}
            onRetry={() => void kitQuery.refetch()}
          />
        )}
        {kitQuery.data && (
          <KitComponentsForm
            key={canonicalStockyPublicBigIntId(
              kitQuery.data.shopify_variant_id,
            )}
            kit={kitQuery.data}
            catalog={catalog}
            parentExistsInShopify={parentExistsInShopify}
            catalogIsReliable={catalogIsReliable}
            replacementSource={replacementSource}
            replacementCheckpoint={replacementCheckpoint}
            onReplacementTargetWrite={onReplacementTargetWrite}
            onReplacementTargetSaved={onReplacementTargetSaved}
            onReplacementCleanupCompleted={
              onReplacementCleanupCompleted
            }
            onReplacementCommitLockChange={onReplacementCommitLockChange}
            onReplacementComplete={onReplacementComplete}
          />
        )}
      </BlockStack>
    </Card>
  );
}

interface EditRow {
  key: number;
  componentShopifyVariantId: string;
  quantityPer: string;
  /** A stale source component must be explicitly replaced before saving. */
  staleSourceLabel: string | null;
}

function isValidQuantity(value: string): boolean {
  const n = Number(value);
  return Number.isInteger(n) && n >= 1;
}

interface KitComponentsFormProps {
  kit: KitBom;
  catalog: BomVariantOption[];
  parentExistsInShopify: boolean;
  catalogIsReliable: boolean;
  replacementSource?: KitBom;
  replacementCheckpoint?: BomTargetCheckpoint;
  onReplacementTargetWrite: (
    sourceKit: KitBom,
    targetId: StockyPublicBigIntId,
    components: BomComponentInput[],
  ) => BomTargetWriteCheckpoint;
  onReplacementTargetSaved: (
    expected: BomTargetWriteCheckpoint,
    sourceKit: KitBom,
    targetId: StockyPublicBigIntId,
    components: BomComponentInput[],
  ) => BomCleanupCheckpoint;
  onReplacementCleanupCompleted: (expected: BomCleanupCheckpoint) => void;
  onReplacementCommitLockChange: (locked: boolean) => void;
  onReplacementComplete: (targetId: StockyPublicBigIntId) => void;
}

function KitComponentsForm({
  kit,
  catalog,
  parentExistsInShopify,
  catalogIsReliable,
  replacementSource,
  replacementCheckpoint,
  onReplacementTargetWrite,
  onReplacementTargetSaved,
  onReplacementCleanupCompleted,
  onReplacementCommitLockChange,
  onReplacementComplete,
}: KitComponentsFormProps) {
  const queryClient = useQueryClient();
  const nextRowKey = useRef(0);
  const kitShopifyVariantId = canonicalStockyPublicBigIntId(
    kit.shopify_variant_id,
  );

  const currentCatalogIds = new Set(
    catalog.map((variant) =>
      canonicalStockyPublicBigIntId(variant.shopify_variant_id),
    ),
  );
  const makeRow = (
    componentShopifyVariantId: string,
    quantityPer: string,
    staleSourceLabel: string | null = null,
  ): EditRow => ({
    key: nextRowKey.current++,
    componentShopifyVariantId,
    quantityPer,
    staleSourceLabel,
  });

  const initialComponents = replacementSource?.components ?? kit.components;

  const [rows, setRows] = useState<EditRow[]>(() => {
    if (replacementCheckpoint !== undefined) {
      return replacementCheckpoint.components.map((component) => {
        const componentId = canonicalStockyPublicBigIntId(
          component.component_shopify_variant_id,
        );
        const isCurrent = currentCatalogIds.has(componentId);
        return makeRow(
          isCurrent ? componentId : "",
          String(component.quantity_per),
          isCurrent ? null : componentId,
        );
      });
    }
    return initialComponents.length > 0
      ? initialComponents.map((c) => {
          const sourceId = canonicalStockyPublicBigIntId(
            c.shopify_variant_id,
          );
          if (replacementSource === undefined) {
            return makeRow(sourceId, String(c.quantity_per));
          }
          const sourceIsCurrent = currentCatalogIds.has(sourceId);
          return makeRow(
            sourceIsCurrent ? sourceId : "",
            String(c.quantity_per),
            sourceIsCurrent
              ? null
              : c.title ?? c.sku ?? sourceId,
          );
        })
      : [makeRow("", "1")];
  });
  const [savedToShopify, setSavedToShopify] = useState(false);
  const [cleanupPending, setCleanupPending] = useState<{
    components: BomComponentInput[];
    error: Error;
  } | null>(() =>
    replacementCheckpoint?.phase === "cleanup"
      ? {
          components: replacementCheckpoint.components,
          error: new Error("stale BOM cleanup is pending"),
        }
      : null,
  );
  const [writeRecoveryComponents, setWriteRecoveryComponents] = useState<
    BomComponentInput[] | null
  >(
    replacementCheckpoint?.phase === "target-write"
      ? replacementCheckpoint.components
      : null,
  );
  const writeRecoveryPending = writeRecoveryComponents !== null;
  const replacementTargetWriteCheckpoint = useRef<BomTargetWriteCheckpoint | null>(
    replacementCheckpoint?.phase === "target-write"
      ? replacementCheckpoint
      : null,
  );
  const replacementCleanupCheckpoint = useRef<BomCleanupCheckpoint | null>(
    replacementCheckpoint?.phase === "cleanup" ? replacementCheckpoint : null,
  );
  const replaceInFlight = useRef(false);

  const replace = useMutation({
    onMutate: async (components: BomComponentInput[]) => {
      if (replacementSource !== undefined) {
        const checkpoint = onReplacementTargetWrite(
          replacementSource,
          kitShopifyVariantId,
          components,
        );
        replacementTargetWriteCheckpoint.current = checkpoint;
        onReplacementCommitLockChange(true);
        setWriteRecoveryComponents(
          components.map((component) => ({ ...component })),
        );
        return { checkpointStored: true, checkpoint };
      }
      return { checkpointStored: false, checkpoint: null };
    },
    mutationFn: async (components: BomComponentInput[]) => {
      await ensureShopifyOptionalScope("write_products");
      const writeCheckpoint = replacementTargetWriteCheckpoint.current;
      if (replacementSource !== undefined && writeCheckpoint === null) {
        throw new BomStaleReplacementCheckpointStorageError(
          "target write checkpoint owner is unavailable",
        );
      }
      const savedKit = replacementSource === undefined
        ? await replaceKitComponentsAndReflect(
            kitShopifyVariantId,
            components,
          )
        : await explicitlyApplyStaleKitReplacementAndReflect(
            replacementSource.shopify_variant_id,
            kitShopifyVariantId,
            components,
            writeCheckpoint!.workflowId,
          );
      if (replacementSource === undefined) {
        return { savedKit, cleanupError: null };
      }
      // If this durable phase transition fails, do not delete the source BOM.
      // The prior target-write checkpoint keeps the exact hardened-write retry.
      const cleanupCheckpoint = onReplacementTargetSaved(
        writeCheckpoint!,
        replacementSource,
        kitShopifyVariantId,
        components,
      );
      replacementCleanupCheckpoint.current = cleanupCheckpoint;
      try {
        // Once the hardened writer confirms Shopify + local convergence, the
        // only remaining operation is local cleanup. Its durable boundary was
        // recorded above before deleting the source BOM.
        await cleanupStaleKitAfterReplacement(
          replacementSource.shopify_variant_id,
          kitShopifyVariantId,
          components,
          cleanupCheckpoint.workflowId,
        );
        onReplacementCleanupCompleted(cleanupCheckpoint);
        return { savedKit, cleanupError: null };
      } catch (error) {
        return {
          savedKit,
          cleanupError:
            error instanceof Error
              ? error
              : new Error("stale BOM cleanup failed"),
        };
      }
    },
    onSuccess: ({ savedKit, cleanupError }, components) => {
      queryClient.setQueryData(
        ["bom-kit", kitShopifyVariantId],
        savedKit,
      );
      setSavedToShopify(true);
      if (replacementSource === undefined) {
        void queryClient.invalidateQueries({ queryKey: KITS_QUERY_KEY });
      } else if (cleanupError !== null) {
        setWriteRecoveryComponents(null);
        setCleanupPending({ components, error: cleanupError });
      } else {
        setWriteRecoveryComponents(null);
        onReplacementCommitLockChange(false);
        onReplacementComplete(kitShopifyVariantId);
      }
    },
    onError: (_error, _components, context) => {
      if (replacementSource !== undefined) {
        if (context?.checkpointStored === true) {
          // The Shopify outcome may be ambiguous. Freeze the exact persisted
          // body and only allow the hardened same-payload retry.
          onReplacementCommitLockChange(true);
        } else {
          onReplacementCommitLockChange(false);
        }
      }
    },
    onSettled: () => {
      replaceInFlight.current = false;
    },
  });

  const retryCleanup = useMutation({
    mutationFn: async (components: BomComponentInput[]) => {
      if (replacementSource === undefined) {
        throw new Error("replacement source is unavailable");
      }
      const cleanupCheckpoint = replacementCleanupCheckpoint.current;
      if (cleanupCheckpoint === null) {
        throw new BomStaleReplacementCheckpointStorageError(
          "cleanup checkpoint owner is unavailable",
        );
      }
      await cleanupStaleKitAfterReplacement(
        replacementSource.shopify_variant_id,
        kitShopifyVariantId,
        components,
        cleanupCheckpoint.workflowId,
      );
      onReplacementCleanupCompleted(cleanupCheckpoint);
    },
    onSuccess: () => {
      setCleanupPending(null);
      void queryClient.invalidateQueries({ queryKey: KITS_QUERY_KEY });
      onReplacementCommitLockChange(false);
      onReplacementComplete(kitShopifyVariantId);
    },
  });

  const currentComponentIds = new Set(
    catalog.map((variant) =>
      canonicalStockyPublicBigIntId(variant.shopify_variant_id),
    ),
  );
  const staleComponentOptions = kit.components
    .filter(
      (component) =>
        !currentComponentIds.has(
          canonicalStockyPublicBigIntId(component.shopify_variant_id),
        ),
    )
    .map((component) => ({
      label: t("{name}（Shopifyに存在しません）", {
        name:
          component.title ??
          component.sku ??
          canonicalStockyPublicBigIntId(component.shopify_variant_id),
      }),
      value: canonicalStockyPublicBigIntId(component.shopify_variant_id),
      disabled: true,
    }));
  const componentOptions = [
    { label: t("選択してください"), value: "" },
    ...staleComponentOptions,
    ...catalog.map((v) => ({
      label: variantLabel(v),
      value: canonicalStockyPublicBigIntId(v.shopify_variant_id),
    })),
  ];

  const updateRow = (key: number, patch: Partial<EditRow>) => {
    setSavedToShopify(false);
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };
  const removeRow = (key: number) => {
    setSavedToShopify(false);
    setRows((prev) => prev.filter((r) => r.key !== key));
  };
  const addRow = () => {
    setSavedToShopify(false);
    setRows((prev) => [...prev, makeRow("", "1")]);
  };

  const selectedRows = rows.filter((r) => r.componentShopifyVariantId !== "");
  const parsedComponents: BomComponentInput[] = selectedRows.map((r) => ({
    component_shopify_variant_id: canonicalStockyPublicBigIntId(
      r.componentShopifyVariantId,
    ),
    quantity_per: Number(r.quantityPer),
  }));

  const hasInvalidQuantity = selectedRows.some((r) => !isValidQuantity(r.quantityPer));
  const hasDuplicate =
    new Set(parsedComponents.map((c) => c.component_shopify_variant_id)).size !==
    parsedComponents.length;
  const hasTooManyComponents =
    parsedComponents.length > MAX_SHOPIFY_BUNDLE_COMPONENTS;
  const hasMissingComponents = selectedRows.some(
    (row) => !currentComponentIds.has(row.componentShopifyVariantId),
  );
  const hasUnreplacedStaleComponents = rows.some(
    (row) =>
      row.staleSourceLabel !== null &&
      !currentComponentIds.has(row.componentShopifyVariantId),
  );
  const replacementCheckpointHasMissingComponents =
    replacementCheckpoint?.components.some(
      (component) =>
        !currentComponentIds.has(
          canonicalStockyPublicBigIntId(
            component.component_shopify_variant_id,
          ),
        ),
    ) ?? false;
  const replacementCheckpointMatchesLocalBom = (() => {
    if (replacementCheckpoint === undefined) return false;
    if (kit.components.length !== replacementCheckpoint.components.length) {
      return false;
    }
    const local = new Map(
      kit.components.map((component) => [
        canonicalStockyPublicBigIntId(component.shopify_variant_id),
        component.quantity_per,
      ]),
    );
    return (
      local.size === kit.components.length &&
      replacementCheckpoint.components.every(
        (component) =>
          local.get(
            canonicalStockyPublicBigIntId(
              component.component_shopify_variant_id,
            ),
          ) === component.quantity_per,
      )
    );
  })();
  const formLockedForCleanup =
    cleanupPending !== null ||
    retryCleanup.isPending ||
    replace.isPending ||
    writeRecoveryPending;
  const replacementTargetHasExistingBom =
    replacementSource !== undefined &&
    kit.components.length > 0 &&
    (replacementCheckpoint === undefined ||
      (replacementCheckpoint.phase === "target-write" &&
        !replacementCheckpointMatchesLocalBom));
  const cleanupTargetDoesNotMatch =
    replacementCheckpoint?.phase === "cleanup" &&
    !replacementCheckpointMatchesLocalBom;
  const isNewEmptyConfiguration =
    parsedComponents.length === 0 && kit.components.length === 0;
  const canSave =
    catalogIsReliable &&
    parentExistsInShopify &&
    !hasMissingComponents &&
    !hasUnreplacedStaleComponents &&
    !replacementTargetHasExistingBom &&
    !isNewEmptyConfiguration &&
    !hasInvalidQuantity &&
    !hasDuplicate &&
    !hasTooManyComponents &&
    !formLockedForCleanup &&
    !replace.isPending;
  const canRetryWriteRecovery =
    writeRecoveryComponents !== null &&
    replacementCheckpoint?.phase === "target-write" &&
    catalogIsReliable &&
    parentExistsInShopify &&
    !replacementCheckpointHasMissingComponents &&
    !replacementTargetHasExistingBom &&
    !replace.isPending;
  const canRetryCleanup =
    cleanupPending !== null &&
    replacementCheckpoint?.phase === "cleanup" &&
    catalogIsReliable &&
    parentExistsInShopify &&
    !replacementCheckpointHasMissingComponents &&
    !cleanupTargetDoesNotMatch &&
    !retryCleanup.isPending;

  const saveErrorMessage = (() => {
    if (!replace.isError) return null;
    if (replace.error instanceof ShopifyOptionalScopeError) {
      return replace.error.code === "scope_declined"
        ? t("Shopify商品へ反映する権限が許可されませんでした。権限を許可してから、もう一度保存してください。")
        : t("Shopifyの権限確認を開けませんでした。Shopify管理画面からUreyukiBoxを開き直してください。");
    }
    return (replace.error as Error).message;
  })();

  // 🔴 構成品0件の保存は「このキットの構成を全部消す」＝取り消せない破壊的操作。
  // 他の破壊的操作（棚卸しの適用・在庫調整・移動の取消）は全部 confirm を通しているのに
  // ここだけ素通りだった＝行を削除して保存した瞬間、警告なく全消えになる。
  const handleSave = () => {
    // Keep the event handler itself fail-closed as well as the button.  This
    // prevents a stale callback or DOM manipulation from starting the scope
    // request while the current catalog cannot be verified.
    if (!canSave || replaceInFlight.current) return;
    if (replacementSource !== undefined) {
      const componentSummary = parsedComponents
        .map((component) => {
          const option = catalog.find(
            (candidate) =>
              canonicalStockyPublicBigIntId(
                candidate.shopify_variant_id,
              ) === component.component_shopify_variant_id,
          );
          return t("・{name} × {quantity}", {
            name:
              option === undefined
                ? component.component_shopify_variant_id
                : variantLabel(option),
            quantity: component.quantity_per,
          });
        })
        .join("\n");
      const ok = window.confirm(
        t(
          "「{name}」へ次のセット構成を保存し、Shopifyの在庫計算へ反映します。",
          {
            name:
              kit.title ??
              kit.sku ??
              canonicalStockyPublicBigIntId(kit.shopify_variant_id),
          },
        ) +
          `\n\n${componentSummary}\n\n` +
          t(
            "在庫数を直接増減・上書きする操作ではありません。販売時にShopifyが原材料在庫を減らします。よろしいですか？",
          ),
      );
      if (!ok) return;
    }
    if (parsedComponents.length === 0 && kit.components.length > 0) {
      const ok = window.confirm(
        t("{name} の構成（原材料 {n} 件）をすべて削除します。", {
          name: kit.title ?? kit.sku ?? String(kit.shopify_variant_id),
          n: kit.components.length,
        }) + t("この操作は取り消せません。よろしいですか？"),
      );
      if (!ok) return;
    }
    replaceInFlight.current = true;
    replace.mutate(parsedComponents);
  };

  const handleRetryWriteRecovery = () => {
    if (
      !canRetryWriteRecovery ||
      replaceInFlight.current ||
      replacementCheckpoint?.phase !== "target-write"
    ) {
      return;
    }
    replaceInFlight.current = true;
    replace.mutate(
      replacementCheckpoint.components.map((component) => ({ ...component })),
    );
  };

  const handleRetryCleanup = () => {
    if (!canRetryCleanup || replacementCheckpoint?.phase !== "cleanup") {
      return;
    }
    retryCleanup.mutate(
      replacementCheckpoint.components.map((component) => ({ ...component })),
    );
  };

  return (
    <BlockStack gap="300">
      {kit.buildable_quantity !== null && (
        <Banner tone={kit.buildable_quantity > 0 ? "success" : "warning"}>
          <p>
            {t("現在の在庫であと {n} 個組み立てられます。", {
              n: kit.buildable_quantity,
            })}
          </p>
        </Banner>
      )}
      {replace.isError && (
        <Banner tone="critical" title={t("保存に失敗しました")}>
          <p>{saveErrorMessage}</p>
        </Banner>
      )}
      {writeRecoveryComponents !== null && cleanupPending === null && (
        <Banner
          tone="warning"
          title={t("同じセット構成で保存を完了してください")}
        >
          <BlockStack gap="200">
            <p>
              {t("結果が不明な保存を別の内容で上書きしません。商品や原材料を変えず、保存済みの同じ内容だけを安全に再試行します。")}
            </p>
            <Button
              loading={replace.isPending}
              disabled={!canRetryWriteRecovery}
              onClick={handleRetryWriteRecovery}
            >
              {t("同じセット構成の保存を再試行")}
            </Button>
          </BlockStack>
        </Banner>
      )}
      {savedToShopify && (
        <Banner tone="success" title={t("Shopify商品へ反映しました")}>
          <p>
            {t("Shopifyが原材料の在庫からセット商品の販売可能数を自動計算します。原材料が売れたときもセット商品の在庫へ追随します。")}
          </p>
        </Banner>
      )}
      {cleanupPending !== null && (
        <Banner
          tone="warning"
          title={t("新しい商品への反映は完了しましたが、旧セットの整理が残っています")}
        >
          <BlockStack gap="200">
            <p>
              {t("新しい商品をもう一度Shopifyへ送る必要はありません。このボタンはUreyukiBox内の旧セットだけを安全に整理します。")}
            </p>
            <Button
              loading={retryCleanup.isPending}
              disabled={!canRetryCleanup}
              onClick={handleRetryCleanup}
            >
              {t("旧セットの整理を再試行")}
            </Button>
          </BlockStack>
        </Banner>
      )}
      {retryCleanup.isError && (
        <Banner tone="critical" title={t("旧セットをまだ整理できませんでした")}>
          <p>{retryCleanup.error.message}</p>
        </Banner>
      )}
      {cleanupPending !== null &&
        (replacementCheckpointHasMissingComponents ||
          cleanupTargetDoesNotMatch) && (
          <Banner
            tone="critical"
            title={t("旧セットを整理する前提を確認できません")}
          >
            <p>
              {t("保存済みの新しいセット構成または原材料が現在のデータと一致しません。旧セットは削除せず、安全に停止しています。")}
            </p>
          </Banner>
        )}
      {!catalogIsReliable && (
        <Banner tone="critical" title={t("現在の商品一覧を確認できないため保存できません")}>
          <p>{t("通信状態を確認し、商品一覧を再読み込みしてから保存してください。")}</p>
        </Banner>
      )}
      {!parentExistsInShopify && (
        <Banner tone="critical" title={t("このキット商品は現在のShopifyに存在しません")}>
          <p>
            {t("Shopifyで商品を作成して同期した後、現在の商品をキットとして選び直してください。")}
          </p>
        </Banner>
      )}
      {hasMissingComponents && (
        <Banner tone="critical" title={t("Shopifyに存在しない原材料があります")}>
          <p>{t("該当行を現在の商品へ選び直すか削除してください。")}</p>
        </Banner>
      )}
      {hasUnreplacedStaleComponents && (
        <Banner tone="critical" title={t("旧商品の原材料を選び直してください")}>
          <p>{t("旧商品のIDは引き継ぎません。各行で現在のShopify商品を明示的に選んでください。")}</p>
        </Banner>
      )}
      {replacementTargetHasExistingBom && (
        <Banner tone="critical" title={t("選んだ商品には別のセット構成があります")}>
          <p>{t("既存のセット構成を上書きしません。上の候補から別の商品を選んでください。")}</p>
        </Banner>
      )}
      {hasDuplicate && (
        <Banner tone="critical" title={t("同じ原材料が複数行にあります")}>
          <p>{t("1つの原材料は1行にまとめてください。")}</p>
        </Banner>
      )}
      {hasTooManyComponents && (
        <Banner tone="critical" title={t("原材料は30件までです")}>
          <p>{t("Shopifyのセット商品に登録できる原材料は最大30件です。")}</p>
        </Banner>
      )}

      <BlockStack gap="200">
        {rows.map((row) => (
          <InlineStack key={row.key} gap="200" blockAlign="end" wrap>
            <div
              data-testid="bom-component-select-control"
              style={{ width: BOM_COMPONENT_SELECT_WIDTH }}
            >
              <Select
                label={t("原材料")}
                options={componentOptions}
                value={row.componentShopifyVariantId}
                disabled={formLockedForCleanup}
                onChange={(value) =>
                  updateRow(row.key, { componentShopifyVariantId: value })
                }
              />
              {row.staleSourceLabel !== null &&
                !currentComponentIds.has(row.componentShopifyVariantId) && (
                <Text as="p" tone="critical" variant="bodySm">
                  {t("旧原材料「{name}」の代わりを選択", {
                    name: row.staleSourceLabel,
                  })}
                </Text>
                )}
            </div>
            <Box width="180px">
              <TextField
                label={t("キット1個あたりの数量")}
                type="number"
                autoComplete="off"
                value={row.quantityPer}
                disabled={formLockedForCleanup}
                onChange={(value) => updateRow(row.key, { quantityPer: value })}
                min="1"
                error={
                  row.componentShopifyVariantId !== "" && !isValidQuantity(row.quantityPer)
                    ? t("1以上の整数で入力")
                    : undefined
                }
              />
            </Box>
            <Button
              onClick={() => removeRow(row.key)}
              disabled={
                row.staleSourceLabel !== null || formLockedForCleanup
              }
            >
              {t("削除")}
            </Button>
          </InlineStack>
        ))}
      </BlockStack>

      <InlineStack gap="200">
        <Button
          onClick={addRow}
          disabled={
            rows.length >= MAX_SHOPIFY_BUNDLE_COMPONENTS ||
            formLockedForCleanup
          }
        >
          {t("原材料を追加")}
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={!canSave}>
          {replacementSource === undefined
            ? t("保存してShopify商品に反映")
            : t("セット構成を保存して在庫へ反映する")}
        </Button>
      </InlineStack>
      <Text as="p" tone="subdued">
        {replacementSource === undefined
          ? t("保存時にShopifyの商品をセット商品として登録します。商品在庫の数量を直接上書きする操作ではありません。初回だけShopifyの商品編集権限の許可が必要です。")
          : t("この操作は在庫数を直接増減・上書きしません。Shopifyが原材料在庫からセット商品の販売可能数を計算できるよう、セット構成だけを反映します。")}
      </Text>
    </BlockStack>
  );
}

interface KitsOverviewCardProps {
  kits: KitBom[];
  currentShopifyVariantIds: ReadonlySet<string>;
  isLoading: boolean;
  isError: boolean;
  /** 商品カタログが1件でもあるか。無ければ上のセレクタも空＝案内の行き先が変わる。 */
  hasCatalog: boolean;
  onRetry: () => void;
  onEdit: (shopifyVariantId: StockyPublicBigIntId) => void;
  onCreateReplacement: (kit: KitBom) => void;
  onDeleteStale: (kit: KitBom) => void;
  creatingReplacementFor: StockyPublicBigIntId | null;
  replacementSourceIds: ReadonlySet<string>;
  replacementWorkflowActive: boolean;
  checkpointUnreadable: boolean;
  deletionBlockedSourceIds: ReadonlySet<string>;
  deletingStaleIds: ReadonlySet<string>;
  staleDeleteErrors: Readonly<Record<string, string>>;
}

function KitsOverviewCard({
  kits,
  currentShopifyVariantIds,
  isLoading,
  isError,
  hasCatalog,
  onRetry,
  onEdit,
  onCreateReplacement,
  onDeleteStale,
  creatingReplacementFor,
  replacementSourceIds,
  replacementWorkflowActive,
  checkpointUnreadable,
  deletionBlockedSourceIds,
  deletingStaleIds,
  staleDeleteErrors,
}: KitsOverviewCardProps) {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <Card>
        <SkeletonBodyText lines={4} />
      </Card>
    );
  }

  if (isError) {
    return (
      <GatedQueryError title={t("キット一覧の取得に失敗しました")} onRetry={onRetry} />
    );
  }

  if (kits.length === 0) {
    // 🔴 カタログが空のときに「上のセレクタで選べ」と言うと行き止まりになる
    //    （そのセレクタも空）＝未同期と「まだ作っていない」を区別する（X6）。
    return hasCatalog ? (
      <Banner tone="info" title={t("設定済みのキットはまだありません")}>
        <p>{t("上のセレクタでキット（組立品）を選び、原材料と数量を設定してください。")}</p>
      </Banner>
    ) : (
      <Banner
        tone="info"
        title={t("商品がまだ同期されていません")}
        action={{ content: t("同期する"), onAction: () => navigate("/dashboard") }}
      >
        <p>{t("在庫同期を実行すると、上のセレクタでキット（組立品）を選べるようになります。")}</p>
      </Banner>
    );
  }

  const isParentCurrent = (kit: KitBom) =>
    currentShopifyVariantIds.has(
      canonicalStockyPublicBigIntId(kit.shopify_variant_id),
    );
  const hasMissingComponent = (kit: KitBom) =>
    kit.components.some(
      (component) =>
        !currentShopifyVariantIds.has(
          canonicalStockyPublicBigIntId(component.shopify_variant_id),
        ),
    );
  const hasStaleKit = kits.some(
    (kit) => !isParentCurrent(kit) || hasMissingComponent(kit),
  );
  const rows = kits.map((kit) => {
    const parentCurrent = isParentCurrent(kit);
    const missingComponent = hasMissingComponent(kit);
    const kitId = canonicalStockyPublicBigIntId(kit.shopify_variant_id);
    const kitIsCreating =
      creatingReplacementFor !== null &&
      canonicalStockyPublicBigIntId(creatingReplacementFor) === kitId;
    return [
      kit.title ?? t("商品名なし"),
      kit.sku ?? "",
      formatNumber(kit.components.length),
      kit.buildable_quantity !== null
        ? formatNumber(kit.buildable_quantity)
        : "-",
      <BlockStack
        key={kitId}
        gap="100"
      >
        <Button
          disabled={
            !parentCurrent ||
            checkpointUnreadable ||
            deletingStaleIds.has(kitId) ||
            replacementSourceIds.has(kitId)
          }
          onClick={() => onEdit(kitId)}
        >
          {t("編集")}
        </Button>
        {!parentCurrent && (
          <>
            <Text as="span" tone="critical" variant="bodySm">
              {t("Shopifyに存在しないキット")}
            </Text>
            <Button
              onClick={() => onCreateReplacement(kit)}
              loading={kitIsCreating}
              disabled={
                creatingReplacementFor !== null ||
                replacementWorkflowActive ||
                checkpointUnreadable ||
                deletingStaleIds.has(kitId)
              }
            >
              {t("Shopifyで商品を作成")}
            </Button>
            <Button
              tone="critical"
              onClick={() => onDeleteStale(kit)}
              loading={deletingStaleIds.has(kitId)}
              disabled={
                checkpointUnreadable ||
                kitIsCreating ||
                deletionBlockedSourceIds.has(kitId)
              }
            >
              {t("UreyukiBoxから削除")}
            </Button>
            {staleDeleteErrors[kitId] !== undefined && (
              <Text as="span" tone="critical" variant="bodySm">
                {staleDeleteErrors[kitId]}
              </Text>
            )}
          </>
        )}
        {parentCurrent && missingComponent && (
          <Text as="span" tone="critical" variant="bodySm">
            {t("原材料の選び直しが必要")}
          </Text>
        )}
      </BlockStack>,
    ];
  });

  return (
    <BlockStack gap="300">
      {hasStaleKit && (
        <Banner tone="critical" title={t("Shopifyに存在しない旧商品を含むセットがあります")}>
          <p>
            {t("旧商品IDをShopifyへ送信しません。Shopifyで商品を作成して同期した後、現在の商品でセット構成を登録し直してください。")}
          </p>
        </Banner>
      )}
      <Card padding="0">
        <DataTable
          columnContentTypes={["text", "text", "numeric", "numeric", "text"]}
          headings={[t("キット"), "SKU", t("原材料数"), t("組立可能数"), ""]}
          rows={rows}
        />
      </Card>
    </BlockStack>
  );
}

export function Bom() {
  return (
    <PlanDataPreviewGate
      feature="inventory_analytics"
      featureName={t(FEATURE_NAME)}
      previewQueryKey={["plan-preview", "bom-kits"]}
      previewQueryFn={async () => [await getPlanPreview("bom_kits")]}
      sections={[
        {
          dataset: "bom_kits",
          title: t("設定済みのキット"),
          columns: [
            { key: "kit", label: t("キット") },
            { key: "sku", label: "SKU" },
            { key: "component_count", label: t("原材料数") },
            { key: "buildable_quantity", label: t("組立可能数") },
          ],
        },
      ]}
    >
      <BomContent />
    </PlanDataPreviewGate>
  );
}
